/**
 * CUSTOM TAGS EDITOR
 *
 * Small controlled chip-strip editor for the generic `tags[]` field on
 * user items — the freeform tag array populated by imports (Airtable /
 * CSV) and the admin Tag Pusher tool. Adds a user-facing way to see
 * and manage those values, which previously lived only in Firestore
 * with no UI hook.
 *
 * Renders every value as a small chip with an × button, plus a text
 * input for adding new values. Enter or comma commits the input as a
 * new chip. Case-insensitive dedupe — typing "Magnetic" when "magnetic"
 * is already present is a no-op that flashes the existing chip.
 *
 * Distinct from the structured Tags section (finish/baseType/etc.)
 * which is managed by `TagSelector`. That one is for polish
 * classification; this one is for freeform user labels.
 *
 * Used by all 8 Add[Type] modals and all 8 Detail modals — the shared
 * component keeps behavior consistent across item types.
 */

import { useState } from 'react';
import { X } from 'lucide-react';

interface CustomTagsEditorProps {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  /** Optional placeholder for the free-text input row. Defaults to a
   *  generic prompt; individual modals may override to hint at what
   *  kinds of freeform labels make sense for that item type. */
  placeholder?: string;
}

export default function CustomTagsEditor({
  value,
  onChange,
  disabled = false,
  placeholder = 'Add a tag and press Enter',
}: CustomTagsEditorProps) {
  const [draft, setDraft] = useState('');
  /** Slug of the chip that briefly flashes when the user tries to add
   *  a duplicate. Empty when no flash active. Cleared on next commit. */
  const [dupeFlashKey, setDupeFlashKey] = useState<string | null>(null);

  const commit = () => {
    const raw = draft.trim();
    if (!raw) return;
    const rawLower = raw.toLowerCase();
    const existingLower = value.map((t) => t.toLowerCase());
    const dupeAt = existingLower.indexOf(rawLower);
    if (dupeAt >= 0) {
      // Flash the existing chip so the user sees why nothing happened,
      // then clear the input so they can type the next one.
      setDupeFlashKey(value[dupeAt]);
      setDraft('');
      setTimeout(() => setDupeFlashKey(null), 800);
      return;
    }
    onChange([...value, raw]);
    setDraft('');
  };

  const remove = (idx: number) => {
    const next = value.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5 min-h-[38px] p-1.5 border border-gray-300 rounded bg-white">
      {value.map((tag, idx) => (
        <span
          key={`${tag}-${idx}`}
          className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full transition-colors ${
            dupeFlashKey === tag
              ? 'bg-amber-200 text-amber-900'
              : 'bg-gray-100 text-gray-700'
          }`}
        >
          {tag}
          {!disabled && (
            <button
              type="button"
              onClick={() => remove(idx)}
              className="rounded-full hover:bg-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-400"
              aria-label={`Remove tag ${tag}`}
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </span>
      ))}
      <input
        type="text"
        value={draft}
        onChange={(e) => {
          const v = e.target.value;
          // Comma also commits — matches muscle memory from tag pickers
          // in other places (Airtable, Notion). Strip the comma before
          // treating the remainder as a new draft.
          if (v.includes(',')) {
            const [before, after] = v.split(/,(.*)/s);
            setDraft(before);
            // Commit synchronously with the current draft value so
            // React doesn't drop the trailing part on a fast typist.
            const raw = before.trim();
            if (raw) {
              const rawLower = raw.toLowerCase();
              const existingLower = value.map((t) => t.toLowerCase());
              if (existingLower.indexOf(rawLower) === -1) {
                onChange([...value, raw]);
              }
            }
            setDraft(after ?? '');
            return;
          }
          setDraft(v);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
            // Convenience: empty-field backspace pops the last chip.
            // Common tag-picker pattern; nothing depends on preserving
            // the last chip through empty-backspace.
            remove(value.length - 1);
          }
        }}
        onBlur={commit}
        disabled={disabled}
        placeholder={value.length === 0 ? placeholder : ''}
        className="flex-1 min-w-[120px] px-1 py-0.5 text-sm border-0 outline-none bg-transparent"
      />
    </div>
  );
}
